// cfoCashTableModel — pure, framework-free builder for the treasurer's 13-week
// cash table (weeks as columns, category rows, bold Ending cash). Mirrors the
// server's `summarize_weeks` semantics exactly (summary-row skip, rolling
// 7-day buckets anchored at as_of, signed amounts from event_type) so the
// category cells reconcile with the authoritative `weeks[]` balance math.
// Beginning / Net / Ending always come from `weeks[]` — never client math.

import type {
  QuickBooksCashPlanningResponse,
  QuickBooksCashPlanningRow,
} from '../../../data/ServerApiClient'

export type CashTableWeekStatus = 'closed' | 'current' | 'forecast'

export type CashTableWeekCol = {
  weekStart: string
  label: string
  isCurrent: boolean
  status: CashTableWeekStatus
  actualNetCashChange: number | null
  forecastActualVariance: number | null
}

/** The nine buckets a planning row can land in. */
export type CashTableCategoryKey =
  | 'collections'
  | 'other_inflows'
  | 'payroll'
  | 'rent'
  | 'insurance'
  | 'vendors_ap'
  | 'debt'
  | 'taxes'
  | 'other'

/** Lines the model computes; never a bucket for a planning row. */
export type CashTableComputedKey =
  | 'beginning'
  | 'net'
  | 'ending'
  | 'op_sources'
  | 'op_uses'
  | 'op_net'
  | 'inv_net'
  | 'fin_net'

export type CashTableRowKey = CashTableCategoryKey | CashTableComputedKey

/**
 * Sources-and-uses class, mirroring the server's `activity_class`.
 * `position` is the beginning/net/ending frame, which belongs to no activity.
 */
export type CashSection = 'operating' | 'investing' | 'financing' | 'position'

export type CashTableLineKind =
  | 'balance'
  | 'inflow'
  | 'outflow'
  | 'total'
  /** A section subtotal — sums the lines above it within one activity. */
  | 'subtotal'
  /**
   * A named child of the category line directly above it — a vendor, a
   * customer, or a kind of labour. Never included in any subtotal: the children
   * already sum to their parent, and the parent is what the subtotal counts.
   */
  | 'child'

export type CashTableCell = {
  amount: number | null
  /** CashPlanningRow ids behind this cell, for the drill. */
  rowIds: string[]
  breachesFloor?: boolean
  /** Ending cash if this week alone lands at the unfavourable end of the model's
   * range. Only set on the ending-cash line. */
  low?: number | null
  high?: number | null
  /** The floor holds on the expected path but breaks at the low end. */
  couldBreachFloor?: boolean
}

export type CashTableLine = {
  key: CashTableRowKey
  label: string
  kind: CashTableLineKind
  cells: CashTableCell[]
  /**
   * Which sources-and-uses block this line belongs to. Absent when the server
   * sent no `activity_class` at all — an older backend — in which case the
   * model falls back to the flat nine-category layout and the table renders
   * exactly as it did before sections existed.
   */
  section?: CashSection
  /**
   * Set only on `child` lines: the sub-schedule entry key, unique within the
   * response. `key` stays the PARENT's category key so it remains inside the
   * closed `CashTableRowKey` union — widening that union would break the
   * exhaustive `rowLabelFor` map and the add-item category picker, which have
   * nothing to do with sub-schedules.
   */
  childKey?: string
  /** `document` | `recurring` | `modeled`, on `child` lines only. */
  provenance?: string
}

export type CashTroughCallout = {
  weekStart: string
  weekLabel: string
  endingCash: number
  message: string
}

export type CashTableModel = {
  columns: CashTableWeekCol[]
  lines: CashTableLine[]
  troughs: CashTroughCallout[]
  openingCashBasis: string | null
}

/**
 * Where each bucket sits when the server has not classified it — the same
 * mapping `cash_activity_class` applies server-side, kept here only as the
 * fallback for rows that predate the field. The server value always wins.
 */
const FALLBACK_SECTION: Record<CashTableCategoryKey, CashSection> = {
  collections: 'operating',
  other_inflows: 'operating',
  payroll: 'operating',
  rent: 'operating',
  insurance: 'operating',
  vendors_ap: 'operating',
  taxes: 'operating',
  other: 'operating',
  // Debt service is the one existing bucket that is not operating.
  debt: 'financing',
}

const SECTION_ORDER: CashSection[] = ['operating', 'investing', 'financing']

const CATEGORY_LINES: Array<{
  key: CashTableCategoryKey
  label: string
  kind: CashTableLineKind
}> = [
  { key: 'collections', label: 'Collections', kind: 'inflow' },
  { key: 'other_inflows', label: 'Other inflows', kind: 'inflow' },
  { key: 'payroll', label: 'Payroll', kind: 'outflow' },
  { key: 'rent', label: 'Rent', kind: 'outflow' },
  { key: 'insurance', label: 'Insurance', kind: 'outflow' },
  { key: 'vendors_ap', label: 'Vendors / AP', kind: 'outflow' },
  { key: 'debt', label: 'Debt', kind: 'outflow' },
  { key: 'taxes', label: 'Taxes', kind: 'outflow' },
  { key: 'other', label: 'Other', kind: 'outflow' },
]

/**
 * True when a line key is one of the nine buckets a planning row lands in.
 *
 * The table only makes those cells clickable, but `CashTableLine.key` is the
 * wide union, so this is what lets the drill's narrower prop type be honoured
 * without a cast — a cast would happily pass 'op_net' through the day someone
 * makes a subtotal selectable.
 */
export const isCashTableCategoryKey = (
  key: CashTableRowKey,
): key is CashTableCategoryKey =>
  CATEGORY_LINES.some((line) => line.key === key)

const num = (value: unknown): number | null => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

/**
 * Strict numeric parse that keeps absent as absent.
 *
 * `num` leans on `Number()`, and `Number(null)` is 0 — fine for a total that is
 * genuinely zero, wrong for an optional field, where it would render a made-up
 * $0 band on closed weeks and could trip the floor check.
 */
const numOrAbsent = (value: unknown): number | null => {
  if (value == null || value === '') return null
  return num(value)
}

/** Mirror of the server's `signed_cash_amount` / the workspace's signedPlanningAmount.
 * "distribution" is load-bearing: an owner draw arrives as a NEGATIVE equity
 * delta and matches no other keyword, so without it the sign flips and a draw
 * reads as cash coming in. Not "draw" — a line-of-credit `debt_draw` is an
 * inflow. This rule lives in THREE places; changing one alone makes the
 * category cells contradict the authoritative Net row. */
export const signedCashAmount = (row: QuickBooksCashPlanningRow): number => {
  const amount = num(row.expected_amount) ?? 0
  const eventType = String(row.event_type || '').toLowerCase()
  const isOutflow =
    eventType.includes('disbursement') ||
    eventType.includes('payable') ||
    eventType.includes('payment') ||
    eventType.includes('capex') ||
    eventType.includes('capital') ||
    eventType.includes('expense') ||
    eventType.includes('payroll') ||
    eventType.includes('outflow') ||
    eventType.includes('distribution')
  if (isOutflow && amount > 0) return -amount
  if (!isOutflow && amount < 0) return -amount
  return amount
}

/** Mirror of the server's `is_cash_week_summary_row` — summary rows would double-count. */
const isSummaryRow = (row: QuickBooksCashPlanningRow): boolean => {
  const source = String(row.source_type || '').toLowerCase()
  return (
    source === 'cash_week_summary' ||
    String(row.event_type || '').toLowerCase() === 'net_operating_cash'
  )
}

/** Mirror of the server's `posted_cash_hint` — the QBO transaction type plus the
 * offsetting non-cash GL account, the only signal a POSTED actual carries about
 * what the cash was for. Empty for every other row class. */
const postedCashHint = (row: QuickBooksCashPlanningRow): string => {
  if (row.row_kind !== 'posted_actual') return ''
  const items = Array.isArray(row.evidence_json) ? row.evidence_json : []
  const field = (name: string): string => {
    for (const item of items) {
      const value = item?.[name]
      if (typeof value === 'string' && value.trim()) return value.trim()
    }
    return ''
  }
  return `${field('transaction_type')} ${field('offset_account')}`.toLowerCase()
}

/** Fallback client-side bucket when the server field is absent (older server).
 * Prefer `row.cash_table_row_key` — the server owns the taxonomy. */
export const treasurerRowKeyForPlanningRow = (
  row: QuickBooksCashPlanningRow,
): CashTableCategoryKey => {
  const serverKey = String(
    row.cash_table_row_key || '',
  ).trim() as CashTableCategoryKey
  if (serverKey && CATEGORY_LINES.some((line) => line.key === serverKey))
    return serverKey
  if (row.row_kind === 'bridge') return 'other'
  const signed = signedCashAmount(row)
  const source = String(row.source_type || '').toLowerCase()
  const key = String(row.source_key || '').toLowerCase()
  const event = String(row.event_type || '').toLowerCase()
  const posted = postedCashHint(row)
  if (signed >= 0) {
    const customerReceipts =
      key.startsWith('ar_receipt:') ||
      source.includes('ar') ||
      source.includes('invoice') ||
      event.includes('receivable') ||
      event.includes('customer') ||
      ((source.includes('gross_activity') ||
        source.includes('monthly_cash_reconciliation')) &&
        event.includes('receipt')) ||
      // Deliberately NOT the bare transaction type "Deposit" — an owner capital
      // contribution is a Deposit too. The offsetting account tells them apart.
      /payment|sales receipt|receivable|income|revenue/.test(posted) ||
      // A chart of accounts that names its income account plainly "Sales",
      // guarded against "Sales Tax Payable" (a refund, not a sale).
      (posted.includes('sales') && !posted.includes('tax'))
    return customerReceipts ? 'collections' : 'other_inflows'
  }
  const haystack = `${key} ${source} ${event} ${String(row.counterparty_name || '').toLowerCase()} ${posted}`
  if (/payroll|wage|salar/.test(haystack)) return 'payroll'
  if (/rent|lease|occupancy/.test(haystack)) return 'rent'
  if (/insurance|premium/.test(haystack)) return 'insurance'
  if (/tax/.test(haystack)) return 'taxes'
  if (/loan|interest|debt|principal/.test(haystack)) return 'debt'
  if (
    /payable|vendor|bill|marketing|software|service|subscription/.test(haystack)
  )
    return 'vendors_ap'
  // A posted check to a named party whose account carries no keyword is still a
  // vendor disbursement — the forecast books the same money to Vendors / AP.
  if (row.row_kind === 'posted_actual' && String(row.counterparty_name || '').trim())
    return 'vendors_ap'
  return 'other'
}

const epochDay = (iso: string): number | null => {
  const [y, m, d] = String(iso || '')
    .slice(0, 10)
    .split('-')
    .map(Number)
  if (!y || !m || !d) return null
  return Math.floor(Date.UTC(y, m - 1, d) / 86_400_000)
}

const weekLabel = (iso: string): string => {
  const [y, m, d] = String(iso || '')
    .slice(0, 10)
    .split('-')
    .map(Number)
  if (!y || !m || !d) return iso
  const date = new Date(Date.UTC(y, m - 1, d))
  return `Wk of ${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })}`
}

export function buildCashTableModel(
  resp:
    | (Pick<QuickBooksCashPlanningResponse, 'weeks' | 'rows' | 'as_of_date'> &
        Partial<Pick<QuickBooksCashPlanningResponse, 'sub_schedules'>>)
    | null
    | undefined,
  minCashFloor: number | null,
): CashTableModel {
  const weeks = resp?.weeks || []
  const firstOpenIndex = weeks.findIndex(
    (week) => week.week_status !== 'closed',
  )
  const columns: CashTableWeekCol[] = weeks.map((week, index) => ({
    weekStart: String(week.week_start || '').slice(0, 10),
    label: weekLabel(String(week.week_start || '')),
    isCurrent: week.week_status
      ? week.week_status === 'current'
      : index === Math.max(0, firstOpenIndex),
    status: ((week.week_status as CashTableWeekStatus) ||
      (index === Math.max(0, firstOpenIndex)
        ? 'current'
        : 'forecast')) as CashTableWeekStatus,
    actualNetCashChange: num(week.actual_net_cash_change),
    forecastActualVariance: num(week.forecast_actual_variance),
  }))
  const columnIndexByStart = new Map(
    columns.map((col, index) => [col.weekStart, index] as const),
  )
  const columnForDate = (iso: string): number | null => {
    // The API sends explicit seven-day buckets for both actual history and
    // forecast. Do not infer from as_of: history deliberately precedes it.
    const day = epochDay(iso)
    if (day == null) return null
    for (const [start, index] of columnIndexByStart) {
      const startDay = epochDay(start)
      if (startDay != null && day >= startDay && day <= startDay + 6)
        return index
    }
    return null
  }

  const emptyCells = (): CashTableCell[] =>
    columns.map(() => ({ amount: null, rowIds: [] }))
  const categoryCells = new Map<CashTableCategoryKey, CashTableCell[]>(
    CATEGORY_LINES.map((line) => [line.key, emptyCells()]),
  )
  const sectionByCategory = new Map<CashTableCategoryKey, CashSection>()
  let serverClassified = false
  for (const row of resp?.rows || []) {
    if (isSummaryRow(row)) continue
    const effective = String(row.expected_date || row.week_start || '').slice(
      0,
      10,
    )
    const columnIndex = columnForDate(effective)
    if (columnIndex == null) continue
    const key = treasurerRowKeyForPlanningRow(row)
    const cells = categoryCells.get(key)
    if (!cells) continue
    const cell = cells[columnIndex]
    cell.amount = (cell.amount ?? 0) + signedCashAmount(row)
    cell.rowIds.push(String(row.id))

    // Learn each bucket's activity from the server rather than assuming it.
    // A single classified row is enough to switch the table into sections;
    // none at all means an older backend and we keep the flat layout.
    const activity = String((row as any).activity_class || '').trim()
    if (activity === 'operating' || activity === 'investing' || activity === 'financing') {
      serverClassified = true
      const seen = sectionByCategory.get(key)
      if (seen == null) {
        sectionByCategory.set(key, activity)
      } else if (seen !== activity) {
        // The bucket's rows disagree. Taking whichever row was iterated last
        // would make the section assignment depend on row order, and could put
        // Debt in a different block than the server's own by_activity rollup —
        // two subtotals in one payload, disagreeing. Fall back to the static
        // map, which is at least deterministic and matches the server default.
        sectionByCategory.set(key, FALLBACK_SECTION[key])
      }
    }
  }

  const balanceCells = (
    field: 'opening_cash' | 'net_cash_change' | 'ending_cash',
  ): CashTableCell[] =>
    weeks.map((week) => ({ amount: num(week[field]), rowIds: [] }))
  const endingCells = balanceCells('ending_cash')
  endingCells.forEach((cell, index) => {
    cell.low = numOrAbsent(weeks[index]?.ending_cash_low)
    cell.high = numOrAbsent(weeks[index]?.ending_cash_high)
  })
  if (minCashFloor != null) {
    for (const cell of endingCells) {
      if (cell.amount != null && cell.amount < minCashFloor) {
        cell.breachesFloor = true
      } else if (cell.low != null && cell.low < minCashFloor) {
        // The expected path clears the floor but the week's own downside does
        // not — the case a treasurer most needs to see BEFORE it happens.
        cell.couldBreachFloor = true
      }
    }
  }

  const sectionFor = (key: CashTableCategoryKey): CashSection =>
    sectionByCategory.get(key) ?? FALLBACK_SECTION[key]

  /** Sum a set of cell arrays column-wise, keeping absent as absent. */
  const sumCells = (sources: CashTableCell[][]): CashTableCell[] =>
    columns.map((_, index) => {
      let total: number | null = null
      for (const cells of sources) {
        const amount = cells[index]?.amount
        if (amount == null) continue
        total = (total ?? 0) + amount
      }
      // A subtotal is a computed roll-up; it carries no drill ids of its own.
      return { amount: total, rowIds: [] }
    })

  const categoryLine = (line: (typeof CATEGORY_LINES)[number]): CashTableLine => ({
    ...line,
    cells: categoryCells.get(line.key)!,
    section: sectionFor(line.key),
  })

  /**
   * Named children under one category, in the order the server ranked them
   * (biggest mover first, modeled remainder last).
   *
   * Children are display-only rows. They are NEVER passed to `sumCells` — they
   * already sum to the parent, so counting them in a subtotal would double the
   * category. That is the single way this feature could silently corrupt the
   * table, so children are built here and only ever spliced into `lines`, never
   * into the `rendered` array the subtotals are computed from.
   */
  const childLines = (
    parent: CashTableLine,
    section: CashSection | undefined,
  ): CashTableLine[] => {
    const entries = resp?.sub_schedules?.[parent.key]
    if (!entries?.length) return []
    return entries.map((entry) => {
      const byWeek = new Map<string, { amount: number | null; rowIds: string[] }>()
      for (const week of entry.by_week || []) {
        const weekStart = String(week.week_start || '').slice(0, 10)
        const parsed = Number(week.amount)
        byWeek.set(weekStart, {
          // `Number('')` is 0 and `Number(null)` is 0; an absent amount must
          // stay absent or the child fabricates a $0 it never reported.
          amount:
            week.amount == null || week.amount === '' || !Number.isFinite(parsed)
              ? null
              : parsed,
          rowIds: week.row_ids || [],
        })
      }
      return {
        key: parent.key,
        childKey: entry.key,
        label: entry.label,
        kind: 'child' as const,
        provenance: entry.provenance,
        section,
        cells: columns.map((column) => {
          const cell = byWeek.get(column.weekStart)
          return { amount: cell?.amount ?? null, rowIds: cell?.rowIds ?? [] }
        }),
      }
    })
  }

  const beginning: CashTableLine = {
    key: 'beginning',
    label: 'Beginning cash',
    kind: 'balance',
    cells: balanceCells('opening_cash'),
    section: 'position',
  }
  const net: CashTableLine = {
    key: 'net',
    label: 'Net cash change',
    kind: 'total',
    cells: balanceCells('net_cash_change'),
    section: 'position',
  }
  const ending: CashTableLine = {
    key: 'ending',
    label: 'Ending cash',
    kind: 'total',
    cells: endingCells,
    section: 'position',
  }

  let lines: CashTableLine[]
  if (!serverClassified) {
    // Older backend: no row carried an activity class, so render exactly what
    // this table rendered before sections existed. This path is not a
    // degradation to be tidied away later — frontend and backend deploy
    // independently, so a new client against an old server is a state that is
    // guaranteed to occur on every rollout.
    // No `section` on ANY line: the renderer draws a heading whenever the section
    // changes, so stamping the fallback would emit OPERATING / FINANCING /
    // OPERATING headings for a server that never classified a single row.
    const flat = (line: (typeof CATEGORY_LINES)[number]): CashTableLine => ({
      ...line,
      cells: categoryCells.get(line.key)!,
    })
    lines = [
      { ...beginning, section: undefined },
      ...CATEGORY_LINES.map(flat).flatMap((line) => [
        line,
        ...childLines(line, undefined),
      ]),
      { ...net, section: undefined },
      { ...ending, section: undefined },
    ]
  } else {
    lines = [beginning]
    for (const section of SECTION_ORDER) {
      const inSection = CATEGORY_LINES.filter(
        (line) => sectionFor(line.key) === section,
      )
      if (!inSection.length) continue
      const rendered = inSection.map(categoryLine)
      // A section with no movement anywhere in the horizon is omitted rather
      // than rendered as a band of dashes. Investing is empty for essentially
      // every workbook today — there is no capex forecast source at all — and
      // a permanently blank block is decoration, not information.
      const hasActivity = rendered.some((line) =>
        line.cells.some((cell) => cell.amount != null && Math.abs(cell.amount) >= 0.005),
      )
      if (!hasActivity && section !== 'operating') continue
      for (const line of rendered) {
        lines.push(line, ...childLines(line, section))
      }

      if (section === 'operating') {
        const sources = rendered.filter((line) => line.kind === 'inflow')
        const uses = rendered.filter((line) => line.kind === 'outflow')
        if (sources.length) {
          lines.push({
            key: 'op_sources',
            label: 'Total operating sources',
            kind: 'subtotal',
            cells: sumCells(sources.map((line) => line.cells)),
            section,
          })
        }
        if (uses.length) {
          lines.push({
            key: 'op_uses',
            label: 'Total operating uses',
            kind: 'subtotal',
            cells: sumCells(uses.map((line) => line.cells)),
            section,
          })
        }
        lines.push({
          key: 'op_net',
          label: 'Net operating cash',
          kind: 'subtotal',
          cells: sumCells(rendered.map((line) => line.cells)),
          section,
        })
      } else {
        lines.push({
          key: section === 'investing' ? 'inv_net' : 'fin_net',
          label: section === 'investing' ? 'Net investing cash' : 'Net financing cash',
          kind: 'subtotal',
          cells: sumCells(rendered.map((line) => line.cells)),
          section,
        })
      }
    }
    lines.push(net, ending)
  }

  const openingCashBasis =
    (weeks[0] as any)?.lineage_json?.opening_cash_basis != null
      ? String((weeks[0] as any).lineage_json.opening_cash_basis)
      : null

  return { columns, lines, troughs: [], openingCashBasis }
}
